import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { TrainerCard } from "@/components/trainers/TrainerCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Search, Filter, CheckCircle, Trophy, TrendingUp, Users, Target, CheckCircle2, ArrowRight, Zap, Shield, Clock, Award, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { generateDirectoryTitle } from "@/utils/seoTitles";
import { ProfessionalPromotionBanner } from "@/components/professionals/ProfessionalPromotionBanner";
import { buildPostalAddressStrict } from "@/utils/structuredData/postalAddress";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";

export default function TrainerDirectory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trainers, setTrainers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [priceRange, setPriceRange] = useState("all");

  useEffect(() => {
    const specialtyParam = searchParams.get('specialty');
    if (specialtyParam) {
      setSpecialtyFilter(specialtyParam);
    }
  }, [searchParams]);

  useEffect(() => {
    loadTrainers();
  }, []);

  const loadTrainers = async () => {
    try {
      // Fetch from both trainer_profiles AND listings table
      const [profilesResult, listingsResult] = await Promise.all([
        supabase
          .from("trainer_profiles")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false }),
        supabase
          .from("listings")
          .select("id, name, slug, city, type, thumbnail_url, images, rating, is_premium, short_description, tags")
          .eq("status", "active")
          .or("type.ilike.%trainer%,type.ilike.%entrenador%,type.ilike.%personal trainer%")
      ]);

      const combinedResults: any[] = [];

      // Add profiles with source marker
      if (profilesResult.data) {
        combinedResults.push(...profilesResult.data.map((profile: any) => ({
          ...profile,
          source: 'profile' as const,
        })));
      }

      // Add listings with source marker (mapped to trainer format)
      if (listingsResult.data) {
        combinedResults.push(...listingsResult.data.map((listing: any) => ({
          id: listing.id,
          slug: listing.slug,
          full_name: listing.name,
          location_city: listing.city || "España",
          location_country: "España",
          specialty: listing.tags || [listing.short_description || "Entrenamiento Personal"],
          years_experience: 0,
          hourly_rate: 0,
          bio: listing.short_description || "",
          profile_image: listing.thumbnail_url || listing.images?.[0],
          subscription_type: listing.is_premium ? "premium" : "free",
          source: 'listing' as const,
        })));
      }

      const shuffled = combinedResults.sort(() => Math.random() - 0.5);
      setTrainers(shuffled);
    } catch (error) {
      console.error("Error loading trainers:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrainers = trainers.filter((trainer) => {
    const matchesSearch =
      trainer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trainer.specialty && trainer.specialty.some((s: string) => s.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (trainer.location_city && trainer.location_city.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCountry = countryFilter === "all" || trainer.location_country === countryFilter;
    const matchesCity = cityFilter === "all" || trainer.location_city?.includes(cityFilter);
    const matchesSpecialty = specialtyFilter === "all" || (trainer.specialty && trainer.specialty.includes(specialtyFilter));

    let matchesPrice = true;
    if (priceRange !== "all") {
      const [min, max] = priceRange.split("-").map(Number);
      matchesPrice = trainer.hourly_rate >= min && trainer.hourly_rate <= max;
    }

    return matchesSearch && matchesCountry && matchesCity && matchesSpecialty && matchesPrice;
  });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Directorio de Entrenadores Personales Certificados",
      description: "Lista de entrenadores personales profesionales y certificados en España",
      numberOfItems: filteredTrainers.length,
      url: "https://ekselencia.com/trainers",
      itemListElement: filteredTrainers.slice(0, 10).map((trainer, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Person",
          name: trainer.full_name,
          jobTitle: "Entrenador Personal Certificado",
          ...(buildPostalAddressStrict({
            streetAddress: trainer.location_address,
            addressLocality: trainer.location_city,
            postalCode: trainer.location_postal_code,
            addressRegion: trainer.location_region,
            addressCountry: trainer.location_country || "ES",
          })
            ? {
                address: buildPostalAddressStrict({
                  streetAddress: trainer.location_address,
                  addressLocality: trainer.location_city,
                  postalCode: trainer.location_postal_code,
                  addressRegion: trainer.location_region,
                  addressCountry: trainer.location_country || "ES",
                }),
              }
            : {}),
          knowsAbout: trainer.specialty,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Servicios de Entrenamiento Personal",
      description: "Conecta con más de 500 entrenadores personales certificados en toda España",
      url: "https://ekselencia.com/trainers",
      areaServed: "ES",
    },
  ];

  // Generate dynamic title based on filters
  const seoTitle = generateDirectoryTitle({
    type: 'Entrenadores Personales',
    city: cityFilter !== 'all' ? cityFilter : undefined,
    activity: specialtyFilter !== 'all' ? specialtyFilter : undefined,
    count: filteredTrainers.length
  });

  return (
    <div className="min-h-screen">
      <SEO 
        title={seoTitle}
        description={`Conecta con ${filteredTrainers.length}+ entrenadores personales certificados cerca de ti. Compara perfiles detallados, especialidades (pérdida de peso, hipertrofia, preparación atlética), precios y disponibilidad. Lee opiniones reales y reserva tu primera sesión hoy.`}
        keywords={[
          "entrenadores personales España",
          "personal trainer certificado",
          "coach deportivo",
          "fitness coach",
          "entrenador pérdida peso",
          "preparador físico",
          "nutrición deportiva",
          "entrenamiento personalizado",
        ]}
        canonical="https://ekselencia.com/trainers"
        structuredData={structuredData}
      />
      <Navigation />
      
      <Breadcrumbs
        items={[
          { name: "Inicio", path: "/" },
          { name: "Entrenadores", path: "/trainers" },
        ]}
      />

      {/* Hero Section Ultra-Optimizado */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5" />
        
        <div className="container relative mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-6 py-3 rounded-full backdrop-blur-sm animate-fade-in">
              <Trophy className="w-5 h-5 text-primary animate-bounce" />
              <span className="font-semibold text-primary">+500 Entrenadores Certificados</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                Transforma Tu Cuerpo
              </span>
              <br />
              <span className="text-foreground">Con el Entrenador Perfecto</span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Encuentra <span className="font-bold text-primary">entrenadores personales certificados</span> especializados en tus objetivos. Compara perfiles, precios y disponibilidad en minutos
            </p>

            <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto pt-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">+500</div>
                <div className="text-sm text-muted-foreground">Entrenadores</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">100%</div>
                <div className="text-sm text-muted-foreground">Certificados</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">24/7</div>
                <div className="text-sm text-muted-foreground">Disponibilidad</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button 
                size="lg" 
                className="text-lg px-8 py-7 bg-gradient-primary hover:opacity-90 transition-all hover:scale-105 shadow-2xl group"
                asChild
              >
                <a href="#entrenadores">
                  <Search className="w-5 h-5 mr-2" />
                  Buscar Entrenador
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-7 border-2 hover:bg-secondary/50 transition-all hover:scale-105 group"
                asChild
              >
                <Link to="/become-trainer">
                  <Trophy className="w-5 h-5 mr-2" />
                  Soy Entrenador
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-4 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Primera sesión gratis</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Sin compromisos</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Pago directo al entrenador</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Por Qué Contratar un Entrenador Personal</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Resultados Reales, Objetivos Alcanzados
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Un entrenador personal multiplica por 3 tus posibilidades de éxito
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Target,
                title: "Plan Personalizado",
                description: "Rutinas diseñadas específicamente para tu nivel, objetivos y disponibilidad. No más programas genéricos."
              },
              {
                icon: Users,
                title: "Motivación Constante",
                description: "Alguien que te impulsa cuando quieres rendirte. El 87% logra sus objetivos con entrenador vs 23% solo."
              },
              {
                icon: Shield,
                title: "Técnica Correcta",
                description: "Evita lesiones aprendiendo la forma correcta desde el día 1. Tu salud es lo primero."
              }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-2 hover:border-primary/50 transition-all hover:shadow-xl group">
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <div id="entrenadores" className="container mx-auto px-4 py-16">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-6">Encuentra Tu Entrenador Ideal</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Input
              placeholder="Buscar por nombre o especialidad..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="col-span-2"
            />

            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                <SelectItem value="España">España</SelectItem>
                <SelectItem value="México">México</SelectItem>
                <SelectItem value="Argentina">Argentina</SelectItem>
              </SelectContent>
            </Select>

            <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Especialidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="Pérdida de peso">Pérdida de peso</SelectItem>
                <SelectItem value="Ganancia muscular">Ganancia muscular</SelectItem>
                <SelectItem value="CrossFit">CrossFit</SelectItem>
                <SelectItem value="Yoga">Yoga</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger>
                <SelectValue placeholder="Precio/hora" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los precios</SelectItem>
                <SelectItem value="0-20">Menos de 20€</SelectItem>
                <SelectItem value="20-40">20€ - 40€</SelectItem>
                <SelectItem value="40-60">40€ - 60€</SelectItem>
                <SelectItem value="60-999">Más de 60€</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-muted-foreground">
            {filteredTrainers.length} entrenadores encontrados
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <Skeleton className="h-48 w-full mb-4" />
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredTrainers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-xl text-muted-foreground mb-4">
                  No se encontraron entrenadores con esos filtros
                </p>
                <Button onClick={() => {
                  setSearchTerm("");
                  setCountryFilter("all");
                  setCityFilter("all");
                  setSpecialtyFilter("all");
                  setPriceRange("all");
                }}>
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredTrainers.map((trainer) => (
                  <TrainerCard
                    key={trainer.id}
                    id={trainer.id}
                    slug={trainer.slug}
                    fullName={trainer.full_name}
                    locationCity={trainer.location_city}
                    locationCountry={trainer.location_country}
                    specialty={trainer.specialty}
                    yearsExperience={trainer.years_experience}
                    hourlyRate={trainer.hourly_rate}
                    bio={trainer.bio}
                    profileImage={trainer.profile_image}
                    subscriptionType={trainer.subscription_type}
                    source={trainer.source}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar with Promotions */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <ProfessionalPromotionBanner type="trainer" />
            </div>
          </aside>
        </div>
      </div>

      {/* Related Interlinks */}
      <div className="container mx-auto px-4 pb-8">
        <RelatedInterlinks 
          text={filteredTrainers.map(t => [t.full_name, t.bio, Array.isArray(t.specialty) ? t.specialty.join(' ') : t.specialty].filter(Boolean).join(' ')).join(' ')} 
          maxLinks={6} 
        />
      </div>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
              <Trophy className="w-5 h-5" />
              <span className="font-semibold">¿Eres Entrenador Personal?</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold">
              Consigue Más Clientes Cada Mes
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Únete a la plataforma #1 de entrenadores personales. Miles de personas buscan entrenadores cada día.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-7 mt-6 group"
              asChild
            >
              <Link to="/become-trainer">
                <Trophy className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Registrarme Como Entrenador
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
